import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import DatePicker from "react-date-picker/dist/entry.nostyle";
import { awaitHelper, updateStoriesWithAlternatives } from "../../../utils";
import { Search } from "../../basic/search.js";
import { FullscreenMediaList } from "../../layout/story-list/fullscreen-media-list";
import { SvgHandler } from "../../svg-handler/svg-handler";
import { LOAD_MORE_LIMIT, STORY_FIELDS } from "../../../constants";
import omit from "@babel/runtime/helpers/objectWithoutProperties";
import { CustomLink } from "../../../components/custom-link";

import closeSvg from "../../../../assets/icons/social/close.svg";
import arrowIcon from "../../../../assets/icons/arrow.svg";

import "./search-page.m.css";

export const SearchPage = ({ data = {}, config }) => {
  const sortOptions = [
    {
      name: "Relevance",
      filterKey: "score"
    },
    {
      name: "Newest",
      filterKey: "latest-published"
    }
  ];

  const [openPanel, handlePanel] = useState(null); // handle for template panel and author
  const [openDate, handleDatePanel] = useState(false); // date popup
  const [stories, storiesHandler] = useState(data.stories); // handle api stories

  const [offset, offSetHandler] = useState(data.stories.length);
  const [limit, handleLimit] = useState(LOAD_MORE_LIMIT);

  const [startDate, handleStartDate] = useState(0);
  const [endDate, handleEndDate] = useState(0);

  const [openDropdownPanel, toggleSortPanel] = useState(false); // to handle relevance and newest sort
  const [selectedSortOption, selectedSortOptionsHandler] = useState(get(sortOptions, [0, "name"])); // to set value of relevance and newest
  const [countStoryTemplate, handleCountStoryTemplate] = useState(7); // to show 3 more in filter list
  const [countAuthor, handleCountAuthor] = useState(7); // to show more in author filter
  const [countSection, handleCountSection] = useState(7); // to handle number of section names to show
  const [numberOfFilters, handleNumberOfFilters] = useState(0);
  const [hideLoadMore, hideLoadMoreHandler] = useState(stories.length >= data.total); // can be a conditional render

  const selectedFilters = useRef({});
  const filterUrl = useRef("");
  const filterPanelRef = useRef(null);
  const { q: query } = data.params;
  const { mountAt } = config;

  const getLoadMore = async () => {
    const mountAtPrefix = mountAt || "";
    const queryString = `${mountAtPrefix}/api/v1/advanced-search?q=${query}&limit=${LOAD_MORE_LIMIT}${
      filterUrl.current
    }&offset=${offset}&fields=${STORY_FIELDS}`;
    const { data } = await awaitHelper((await fetch(queryString)).json());
    storiesHandler(updateStoriesWithAlternatives(stories.concat(data.items), false));

    offSetHandler(limit + offset);
    hideLoadMoreHandler(data.items.length === 0 || data.items.length >= data.total);
    handleLimit(limit + LOAD_MORE_LIMIT);
  };

  const handleOpenPanel = () => filterPanelRef.current.classList.toggle("close-panel");

  const moreOptionsStoryTemplate = event => {
    event.preventDefault();
    event.stopPropagation();
    handleCountStoryTemplate(countStoryTemplate + 5);
  };

  const moreOptionsAuthor = event => {
    event.preventDefault();
    event.stopPropagation();
    handleCountAuthor(countAuthor + 5);
  };

  const moreOptionsSection = event => {
    event.preventDefault();
    event.stopPropagation();
    handleCountSection(countSection + 5);
  };

  const openFilterPanel = (event, index) => {
    event.stopPropagation();
    event.preventDefault();
    let activeIndex = openPanel === index ? null : index;
    handlePanel(activeIndex);
  };

  const opendatePanel = event => {
    event.stopPropagation();
    event.preventDefault();
    handleDatePanel(!openDate);
  };

  const handleSortChange = event => {
    event.preventDefault();
    event.stopPropagation();
    if (event.target.getAttribute("data-item") === "list-item") {
      const { name, filterKey } = JSON.parse(event.target.getAttribute("data-value"));
      selectedSortOptionsHandler(name);
      toggleSortPanel(false);
      setIfSelected(event, filterKey);
      filterUrl.current = generateUrl();
      getMeStories(filterUrl.current, mountAt);

      return;
    }
    toggleSortPanel(!openDropdownPanel);
  };

  const handleStartDateChange = startDate => {
    if (!startDate) {
      selectedFilters.current = omit(selectedFilters.current, ["published-after"]);
      filterUrl.current = generateUrl();
      getMeStories(filterUrl.current, mountAt);
      return;
    }
    selectedFilters.current = {
      ...selectedFilters.current,
      "published-after": { [startDate && startDate.getTime()]: true }
    };
    filterUrl.current = generateUrl();
    getMeStories(filterUrl.current, mountAt);
    handleStartDate(startDate);
    handleNumberOfFilters(numberOfFilters + 1);
  };

  const handleEndDateChange = endDate => {
    if (!endDate) {
      selectedFilters.current = omit(selectedFilters.current, ["published-before"]);
      filterUrl.current = generateUrl();
      getMeStories(filterUrl.current, mountAt);
      return;
    }
    selectedFilters.current = {
      ...selectedFilters.current,
      "published-before": { [endDate && endDate.getTime()]: true }
    };
    filterUrl.current = generateUrl();
    getMeStories(filterUrl.current, mountAt);
    handleEndDate(endDate);
    handleNumberOfFilters(numberOfFilters + 1);
  };

  const getFilterOptionName = filterOption => {
    switch (filterOption) {
      case "story-template":
        return "Story Type";
      case "author-name":
        return "Author";
      case "section-name":
        return "Section";
    }
  };

  const enableAdvancedSearch = get(config, ["theme-attributes", "enableAdvancedSearch"]) === "true";

  const getFilterElements = (filterOption, filterValue = {}) => {
    const filterKeys = (filterValue && Object.keys(filterValue)) || [];

    const hideMoreStoryTemplate = countStoryTemplate >= filterKeys.length ? "hide-more-btn" : "";
    const hideMoreAuthor = countAuthor >= filterKeys.length ? "hide-more-btn" : "";
    const hideMoreSection = countSection >= filterKeys.length ? "hide-more-btn" : "";
    switch (filterOption) {
      case "story-template":
        return (
          <div styleName="submenu-list">
            <label styleName="container">
              All
              <input type="radio" name="action" data-parent={"type"} value="ignore" />
              <span styleName="radio-checkmark" />
            </label>
            {filterKeys
              .sort()
              .slice(0, countStoryTemplate)
              .map((filterStoryKey, index) => (
                <div key={`radio-filter-${index}`}>
                  <label styleName="container">
                    {filterStoryKey.replace(/-/g, " ")}
                    <input type="radio" name="action" data-parent={"type"} value={filterStoryKey} />
                    <span styleName="radio-checkmark" />
                  </label>
                </div>
              ))}
            <div styleName={`more-option ${hideMoreStoryTemplate}`} onClick={moreOptionsStoryTemplate}>
              {filterKeys.length - countStoryTemplate} More
            </div>
          </div>
        );
      case "author-name":
        return (
          <div styleName="submenu-list">
            {filterKeys
              .sort()
              .slice(0, countAuthor)
              .map((filterAuthorKey, index) => (
                <div key={`check-filter-${index}`}>
                  <label styleName="container">
                    {filterAuthorKey}
                    <input type="checkbox" data-parent={"author"} value={filterAuthorKey} />
                    <span styleName="checkmark" />
                  </label>
                </div>
              ))}
            <div styleName={`more-option ${hideMoreAuthor}`} onClick={event => moreOptionsAuthor(event)}>
              {filterKeys.length - countAuthor} More
            </div>
          </div>
        );
      case "section-name":
        return (
          <div styleName="submenu-list">
            {filterKeys
              .sort()
              .slice(0, countSection)
              .map((filterSectionKey, index) => (
                <div key={`check-filter-${index}`}>
                  <label styleName="container">
                    {filterSectionKey}
                    <input type="checkbox" data-parent={"section-name"} value={filterSectionKey} />
                    <span styleName="checkmark" />
                  </label>
                </div>
              ))}
            <div styleName={`more-option ${hideMoreSection}`} onClick={event => moreOptionsSection(event)}>
              {filterKeys.length - countSection} More
            </div>
          </div>
        );
    }
  };
  const showDate = () => (
    <div>
      <div onClick={opendatePanel} styleName={openDate ? "panel-data border" : "panel-data"}>
        <div styleName="panel-date">Date</div>
        <span styleName={openDate ? "rotate-arrow-svg" : ""}>
          <SvgHandler xlinkHref={arrowIcon} width={"24px"} height={"24px"} />
        </span>
      </div>
      {openDate && (
        <div styleName="inner-date">
          <DatePicker onChange={handleStartDateChange} value={startDate} />
          <span styleName="to-keyword">to</span>
          <DatePicker onChange={handleEndDateChange} value={endDate} />
        </div>
      )}
    </div>
  );

  const setIfSelected = (event, customValue) => {
    const dataParent = event.target.getAttribute("data-parent");
    if (!dataParent) return null;
    if (event.target.value === "ignore") {
      selectedFilters.current = omit(selectedFilters.current, [dataParent]);
      return;
    }
    const initialSelectedValue = selectedFilters.current[dataParent] || {};
    const triggeredObject = {
      [event.target.value || customValue]: true
    };
    const modifiedValue =
      event.target.getAttribute("type") === "checkbox"
        ? { ...initialSelectedValue, ...triggeredObject }
        : triggeredObject;

    selectedFilters.current = {
      ...selectedFilters.current,
      [dataParent]: modifiedValue
    };
  };

  const setIfDeselected = event => {
    const dataParent = event.target.getAttribute("data-parent");
    if (!dataParent) return null;
    const initialSelectedValue = selectedFilters.current[dataParent] || {};
    event.target.value in initialSelectedValue && delete initialSelectedValue[event.target.value];
    if (Object.keys(initialSelectedValue).length === 0) {
      selectedFilters.current = omit(selectedFilters.current, [dataParent]);
    } else {
      selectedFilters.current = {
        ...selectedFilters.current
      };
    }
  };

  const generateUrl = () => {
    return Object.keys(selectedFilters.current).reduce((acc, selectedFilter) => {
      const objKeys = Object.keys(selectedFilters.current[selectedFilter]);
      if (objKeys.length) {
        const selectedKeys = objKeys.join(",");
        acc += `&${selectedFilter}=${selectedKeys}`;
      }
      return acc;
    }, "");
  };

  // handle event delegation
  const handleFilterClicks = event => {
    event.stopPropagation();
    if (event.target.checked) {
      setIfSelected(event);
    } else {
      setIfDeselected(event);
    }
    filterUrl.current = generateUrl();
    getMeStories(filterUrl.current, mountAt);
  };

  const getMeStories = async (url, mountAt) => {
    try {
      const mountAtPrefix = mountAt || "";
      const queryString = `${mountAtPrefix}/api/v1/advanced-search?q=${query}${url}&fields=${STORY_FIELDS}`;
      const { data } = await awaitHelper((await fetch(queryString)).json());
      storiesHandler(data.items);
      offSetHandler(limit + offset);
      hideLoadMoreHandler(data.items.length >= data.total);
      handleLimit(limit + 4);
    } catch (error) {
      console.log(error);
    }
  };

  const getNumberOfFilters = () => {
    const filterNumber = Object.keys(selectedFilters.current).filter(
      selectedFilter =>
        !(
          "score" in selectedFilters.current[selectedFilter] ||
          "latest-published" in selectedFilters.current[selectedFilter]
        )
    ).length;
    if (filterNumber === 0) return null;
    return <span styleName="filter">{filterNumber}</span>;
  };

  const filterOption = data => {
    const filterOptions = get(data, ["facets"]) || {};

    return (
      <div onClick={handleFilterClicks}>
        {Object.keys(filterOptions).map((filterOption, index) => (
          <>
            <div
              key={index}
              styleName={`panel-data ${index === openPanel ? "border" : ""}`}
              onClick={event => openFilterPanel(event, index)}
            >
              <div styleName="panel-option-name">{getFilterOptionName(filterOption)}</div>
              <span styleName={index === openPanel ? "rotate-svg" : ""}>
                <SvgHandler xlinkHref={arrowIcon} width={"24px"} height={"24px"} />
              </span>
            </div>
            <div styleName={`sub-list ${index === openPanel ? "open" : ""}`}>
              {getFilterElements(filterOption, filterOptions[filterOption])}
            </div>
          </>
        ))}
      </div>
    );
  };

  const isDateEnable = get(config, ["theme-attributes", "filterOption", "date"], true);

  const clearBtn = Object.keys(selectedFilters.current).filter(
    selectedFilter => !("score" in selectedFilters.current[selectedFilter])
  ).length;
  const isFilterEnable = enableAdvancedSearch ? "update-height" : "";
  return (
    <div styleName="page-wrapper">
      <div className="component-wrapper" styleName="search-page-wrapper">
        <div styleName={`story-list ${isFilterEnable}`}>
          <div>
            <Search q={data.params.q} />
            {enableAdvancedSearch && (
              <div styleName="header-option">
                <p>Sort by</p>

                <div styleName="dropdownWrapper">
                  <div>
                    <div data-item="list" styleName="select-wrapper" onClick={handleSortChange}>
                      <div styleName="placeholder">{selectedSortOption}</div>
                      {openDropdownPanel && (
                        <ul styleName="select">
                          {sortOptions.map((item, index) => (
                            <li
                              styleName="list"
                              key={`dropdown-${index}`}
                              data-value={JSON.stringify(item)}
                              data-parent={"sort"}
                              data-item="list-item"
                            >
                              {item.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>

                <div styleName="filter-btn" onClick={handleOpenPanel}>
                  <span>Filter{getNumberOfFilters()}</span>
                </div>
              </div>
            )}
          </div>
          <div styleName="stories">
            {stories.length ? (
              <FullscreenMediaList
                limit={limit}
                stories={updateStoriesWithAlternatives(stories, false)}
                config={config}
                hideLoadmore={hideLoadMore}
                getMoreStories={getLoadMore}
              />
            ) : (
              <div className="component-wrapper">
                <p styleName="no-more-result">
                  No stories found for &#39;<strong>{query}</strong>&#39; .
                </p>
              </div>
            )}
          </div>
        </div>

        {enableAdvancedSearch && (
          <div ref={filterPanelRef} styleName="full-height-panel" id="panel">
            <div styleName="close-wrapper" onClick={handleOpenPanel}>
              <SvgHandler xlinkHref={closeSvg} clazzName="close" width="24px" height="24px" />
            </div>
            <div styleName="clear-filter">
              Filter
              <div styleName="clear-wrapper">
                {clearBtn === 0 ? null : (
                  <CustomLink href={`search?q=${query}`} styleName="clear-btn">
                    Clear
                  </CustomLink>
                )}
              </div>
            </div>
            <div>
              {filterOption(data)}
              {isDateEnable && showDate()}
              <div onClick={handleOpenPanel} styleName="filter-wrapper">
                <button styleName="save-btn">Search</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SearchPage.propTypes = {
  data: PropTypes.any,
  config: PropTypes.any,
  numberOfStoriesHandler: PropTypes.func
};
